/**
 * **El cajón de la grabación** («Grabar mejora», H6): cada trozo de 5 s se guarda al instante en el
 * navegador (IndexedDB), para que recargar, cerrar la pestaña o que se caiga no pierda el video. Se
 * borra solo cuando el servidor confirma que la mejora quedó guardada, o si la persona la descarta.
 *
 * ```
 * grabacion  { id, inicio, ultimo, tipo, ruta, porTope, ms? }   una fila por grabación
 * trozo      { id, n, blob }                                    sus trozos, en orden
 * ```
 *
 * Todo va en try/catch: sin IndexedDB (ventana privada, sitio bloqueado) la grabadora sigue
 * funcionando como antes, solo en memoria.
 */

export type Grabacion = {
  id: string;
  /** Cuándo empezó (ms de `Date.now()`). */
  inicio: number;
  /** El último trozo guardado: si pasa mucho sin trozos, la grabación quedó a medias. */
  ultimo: number;
  tipo: string;
  ruta: string;
  porTope: boolean;
  /** La duración final, si alcanzó a detenerse. */
  ms?: number;
};

export type Recuperada = { grabacion: Grabacion; trozos: Blob[] };

const BASE = "neca-mejoras";
const GRABACION = "grabacion";
const TROZO = "trozo";
/** Tres trozos sin noticias: la pestaña que grababa ya no está (otra pestaña viva no se toca). */
const ABANDONADA_MS = 15_000;

function abrir(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(BASE, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(GRABACION, { keyPath: "id" });
      req.result.createObjectStore(TROZO, { keyPath: ["id", "n"] });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function pedir<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function terminar(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = tx.onabort = () => reject(tx.error);
  });
}

const deTrozos = (id: string) => IDBKeyRange.bound([id, 0], [id, Number.MAX_SAFE_INTEGER]);

async function seguro<T>(fn: (db: IDBDatabase) => Promise<T>, siFalla: T): Promise<T> {
  let db: IDBDatabase | undefined;
  try {
    db = await abrir();
    return await fn(db);
  } catch {
    return siFalla;
  } finally {
    db?.close();
  }
}

/** Guarda la fila de la grabación y, si llega, su trozo `n`, en una sola transacción. */
export function anotar(grabacion: Grabacion, trozo?: { n: number; blob: Blob }): Promise<boolean> {
  return seguro(async (db) => {
    const tx = db.transaction([GRABACION, TROZO], "readwrite");
    tx.objectStore(GRABACION).put(grabacion);
    if (trozo) tx.objectStore(TROZO).put({ id: grabacion.id, n: trozo.n, blob: trozo.blob });
    await terminar(tx);
    return true;
  }, false);
}

/**
 * Una grabación que quedó sin guardar (detenida, o a medias y abandonada) con sus trozos en orden.
 * Las que no tienen ningún trozo se limpian.
 */
export function pendiente(ahora = Date.now()): Promise<Recuperada | null> {
  return seguro(async (db) => {
    const filas = await pedir(db.transaction(GRABACION).objectStore(GRABACION).getAll()) as Grabacion[];
    for (const grabacion of filas.sort((a, b) => a.inicio - b.inicio)) {
      if (grabacion.ms === undefined && ahora - grabacion.ultimo < ABANDONADA_MS) continue;
      const trozos = (await pedir(db.transaction(TROZO).objectStore(TROZO).getAll(deTrozos(grabacion.id))) as { n: number; blob: Blob }[])
        .sort((a, b) => a.n - b.n)
        .map((t) => t.blob);
      if (trozos.length) return { grabacion, trozos };
      await borrarEn(db, grabacion.id);
    }
    return null;
  }, null);
}

function borrarEn(db: IDBDatabase, id: string) {
  const tx = db.transaction([GRABACION, TROZO], "readwrite");
  tx.objectStore(GRABACION).delete(id);
  tx.objectStore(TROZO).delete(deTrozos(id));
  return terminar(tx);
}

/** La grabación ya está en el servidor, o se descartó: fuera del navegador. */
export function olvidar(id: string): Promise<void> {
  return seguro((db) => borrarEn(db, id), undefined);
}
